/**
 * Tabbed icon + emoji picker.
 *
 * Spec: `categories` — Category Icon Picker With Tabs Search and Groups.
 * Emits `lucide:<name>` or `emoji:<char>` strings via `onChange`.
 */
import { useMemo, useState, type CSSProperties } from 'react';
import Icon from './Icon';
import { LUCIDE_CATALOG, type LucideIconEntry } from './iconCatalog';
import { EMOJI_CATALOG, type EmojiEntry } from './emojiCatalog';

export interface IconPickerProps {
  value: string;
  onChange: (next: string) => void;
  autoFocus?: boolean;
}

type Tab = 'lucide' | 'emoji';

const GROUP_LABELS_TW: Readonly<Record<string, string>> = {
  food: '飲食',
  transport: '交通',
  home: '居家',
  lifestyle: '生活',
  entertainment: '娛樂',
  health: '健康',
  pet: '寵物',
  travel: '旅遊',
  finance: '財務',
  work: '工作',
  misc: '其他',
  activity: '休閒',
  object: '物品',
  symbol: '符號',
  animal: '動物',
};

function groupLabel(group: string): string {
  return GROUP_LABELS_TW[group] ?? group;
}

function matchesQuery(haystack: ReadonlyArray<string>, query: string): boolean {
  if (query.length === 0) return true;
  const q = query.trim().toLowerCase();
  if (q.length === 0) return true;
  return haystack.some((s) => s.toLowerCase().includes(q));
}

function lucideMatches(entry: LucideIconEntry, query: string): boolean {
  return matchesQuery([entry.name, ...entry.keywords], query);
}

function emojiMatches(entry: EmojiEntry, query: string): boolean {
  return matchesQuery([entry.char, ...entry.keywords], query);
}

function groupBy<T>(items: ReadonlyArray<T>, key: (t: T) => string): Array<[string, T[]]> {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const k = key(item);
    const bucket = map.get(k) ?? [];
    bucket.push(item);
    map.set(k, bucket);
  }
  return Array.from(map.entries());
}

const tabStyle: CSSProperties = {
  flex: 1,
  padding: '8px 12px',
  borderTop: 'none',
  borderLeft: 'none',
  borderRight: 'none',
  borderBottomStyle: 'solid',
  borderBottomWidth: 2,
  borderBottomColor: 'transparent',
  background: 'transparent',
  cursor: 'pointer',
  font: '500 13px var(--font-cjk)',
  color: 'var(--fg-2)',
};

const tabActiveStyle: CSSProperties = {
  color: 'var(--accent)',
  borderBottomColor: 'var(--accent)',
};

const searchStyle: CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid var(--fg-5, #ddd)',
  font: '400 14px var(--font-cjk)',
  marginTop: 8,
  marginBottom: 8,
  outline: 'none',
};

const groupHeaderStyle: CSSProperties = {
  font: '600 11px var(--font-cjk)',
  color: 'var(--fg-3)',
  letterSpacing: '0.05em',
  margin: '12px 2px 6px',
};

const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(44px, 1fr))',
  gap: 6,
};

const cellBase: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 44,
  height: 44,
  borderRadius: 10,
  borderStyle: 'solid',
  borderWidth: 1,
  borderColor: 'transparent',
  background: 'var(--fg-7, #f3f3f3)',
  cursor: 'pointer',
  padding: 0,
};

const cellActive: CSSProperties = {
  borderColor: 'var(--accent)',
  background: 'var(--accent-bg, rgba(0,0,0,0.05))',
};

const hintBtnStyle: CSSProperties = {
  marginTop: 12,
  padding: '6px 12px',
  border: '1px solid var(--fg-5, #ddd)',
  background: 'transparent',
  borderRadius: 8,
  cursor: 'pointer',
  font: '500 12px var(--font-cjk)',
  color: 'var(--fg-2)',
};

export default function IconPicker({ value, onChange, autoFocus }: IconPickerProps): JSX.Element {
  const initialTab: Tab = value.startsWith('emoji:') ? 'emoji' : 'lucide';
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const [search, setSearch] = useState('');

  const lucideHits = useMemo(
    () => LUCIDE_CATALOG.filter((e) => lucideMatches(e, search)),
    [search],
  );
  const emojiHits = useMemo(
    () => EMOJI_CATALOG.filter((e) => emojiMatches(e, search)),
    [search],
  );

  const activeHits = activeTab === 'lucide' ? lucideHits.length : emojiHits.length;
  const otherTab: Tab = activeTab === 'lucide' ? 'emoji' : 'lucide';
  const otherHits = activeTab === 'lucide' ? emojiHits.length : lucideHits.length;
  const showSwitchHint = search.trim().length > 0 && activeHits === 0 && otherHits > 0;

  return (
    <div>
      <div role="tablist" style={{ display: 'flex', borderBottom: '1px solid var(--fg-6, #eee)' }}>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'lucide'}
          onClick={() => setActiveTab('lucide')}
          style={{ ...tabStyle, ...(activeTab === 'lucide' ? tabActiveStyle : {}) }}
        >
          圖示
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'emoji'}
          onClick={() => setActiveTab('emoji')}
          style={{ ...tabStyle, ...(activeTab === 'emoji' ? tabActiveStyle : {}) }}
        >
          Emoji
        </button>
      </div>

      <input
        type="search"
        role="searchbox"
        placeholder="搜尋圖示或關鍵字..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={searchStyle}
        autoFocus={autoFocus}
        aria-label="搜尋圖示"
      />

      {activeTab === 'lucide' ? (
        <LucideTab items={lucideHits} value={value} onChange={onChange} />
      ) : (
        <EmojiTab items={emojiHits} value={value} onChange={onChange} />
      )}

      {activeHits === 0 && (
        <div style={{ padding: '24px 8px', textAlign: 'center', color: 'var(--fg-3)' }}>
          找不到符合的圖示
          {showSwitchHint && (
            <div>
              <button
                type="button"
                style={hintBtnStyle}
                onClick={() => setActiveTab(otherTab)}
                aria-label={`${otherTab === 'emoji' ? 'Emoji' : '圖示'} 也有 ${otherHits} 個結果`}
              >
                {otherTab === 'emoji' ? 'Emoji' : '圖示'} 也有 {otherHits} 個結果
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LucideTab(props: {
  items: ReadonlyArray<LucideIconEntry>;
  value: string;
  onChange: (next: string) => void;
}): JSX.Element {
  const groups = groupBy(props.items, (e) => e.group);
  return (
    <div>
      {groups.map(([group, entries]) => (
        <section key={group}>
          <div style={groupHeaderStyle}>{groupLabel(group)}</div>
          <div style={gridStyle}>
            {entries.map((e) => {
              const next = `lucide:${e.name}`;
              const selected = props.value === next;
              return (
                <button
                  key={e.name}
                  type="button"
                  onClick={() => props.onChange(next)}
                  aria-label={`使用圖示：${e.keywords[0] ?? e.name} (${e.name})`}
                  aria-pressed={selected}
                  style={{ ...cellBase, ...(selected ? cellActive : {}) }}
                >
                  <Icon value={next} size={22} stroke="var(--fg-1)" />
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

function EmojiTab(props: {
  items: ReadonlyArray<EmojiEntry>;
  value: string;
  onChange: (next: string) => void;
}): JSX.Element {
  const groups = groupBy(props.items, (e) => e.group);
  return (
    <div>
      {groups.map(([group, entries]) => (
        <section key={group}>
          <div style={groupHeaderStyle}>{groupLabel(group)}</div>
          <div style={gridStyle}>
            {entries.map((e) => {
              const next = `emoji:${e.char}`;
              const selected = props.value === next;
              return (
                <button
                  key={e.char}
                  type="button"
                  onClick={() => props.onChange(next)}
                  aria-label={`使用表情：${e.keywords[0] ?? e.char}`}
                  aria-pressed={selected}
                  style={{ ...cellBase, ...(selected ? cellActive : {}) }}
                >
                  <span style={{ fontSize: 22, lineHeight: 1 }}>{e.char}</span>
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
